// App.js
import './App.css';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import RSVP from './pages/RSVP';
import MenuChoices from './pages/MenuChoices';
import Accommodation from './pages/Accommodation';
import Location from './pages/Location';
import PhotoUpload from './pages/PhotoUpload';
import Login from './pages/Login';
import Logout from './pages/Logout';
import HomePage from './pages/Homepage'; // Import the new HomePage component
import { auth, db } from "./firebaseConfig";
import { collection, getDocs, query, where } from 'firebase/firestore';
import AddGuest from './pages/AddGuest';
import GuestList from './pages/GuestList';
import UserLogs from './pages/UserLogs';
import FAQ from './pages/FAQ';
import FAQEdit from './pages/FAQEdit';
import { requestNotificationPermission } from './utils/notifications';
import { messaging, onMessage } from './firebaseConfig';
import { logPageView, logPageExit, attachClickTracker } from './utils/tracking';

function AppContent({ user, menuOpen, setMenuOpen, toggleMenu }) {
  const location = useLocation();
  const pageEnteredAtRef = useRef(null);
  const lastPathnameRef = useRef(null);
  const [showMenuChoicesInNav, setShowMenuChoicesInNav] = useState(true);

  // Log page view on route change; log exit for previous page with duration
  useEffect(() => {
    const pathname = location.pathname;
    const now = Date.now();
    if (lastPathnameRef.current != null && pageEnteredAtRef.current != null) {
      const duration = (now - pageEnteredAtRef.current) / 1000;
      logPageExit(lastPathnameRef.current, duration);
    }
    logPageView(pathname);
    lastPathnameRef.current = pathname;
    pageEnteredAtRef.current = now;
  }, [location.pathname]);

  // On tab close or tab hide: log page exit
  useEffect(() => {
    const onExit = () => {
      if (lastPathnameRef.current != null && pageEnteredAtRef.current != null) {
        const duration = (Date.now() - pageEnteredAtRef.current) / 1000;
        logPageExit(lastPathnameRef.current, duration);
      }
    };
    window.addEventListener('beforeunload', onExit);
    const visHandler = () => { if (document.visibilityState === 'hidden') onExit(); };
    document.addEventListener('visibilitychange', visHandler);
    return () => {
      window.removeEventListener('beforeunload', onExit);
      document.removeEventListener('visibilitychange', visHandler);
    };
  }, []);

  // Global click tracker (throttled in tracking.js)
  useEffect(() => {
    return attachClickTracker();
  }, []);

  // Hide Menu Choices nav link for evening-only families (they don't select meals)
  useEffect(() => {
    if (!user?.phoneNumber) {
      setShowMenuChoicesInNav(true);
      return;
    }
    const checkFamilyHasDayGuests = async () => {
      try {
        const invitesRef = collection(db, 'invites');
        const userSnap = await getDocs(query(invitesRef, where('phoneNumber', '==', user.phoneNumber)));
        if (userSnap.empty) {
          setShowMenuChoicesInNav(true);
          return;
        }
        const familyClan = userSnap.docs[0].data().familyClan;
        const clanSnap = await getDocs(query(invitesRef, where('familyClan', '==', familyClan)));
        const hasDayGuests = clanSnap.docs.some(doc => !doc.data().eveningGuest);
        setShowMenuChoicesInNav(hasDayGuests);
      } catch (err) {
        console.error('Error checking evening guests:', err);
        setShowMenuChoicesInNav(true);
      }
    };
    checkFamilyHasDayGuests();
  }, [user?.phoneNumber]);

  const ProtectedRoute = ({ children }) => {
    const loc = useLocation();
    return user ? children : <Navigate to="/login" state={{ from: loc }} />;
  };

  const isAdmin = user && ['+447720378051', '+447720378050', '+447415214865'].includes(user.phoneNumber);

  return (
    <div className="App">
        {/* Decorative floral images - fixed position */}
        <img 
          src="/decorative-floral.png" 
          alt="" 
          className="decorative-floral decorative-floral-bottom-left"
          aria-hidden="true"
          onError={(e) => {
            // Fallback if image doesn't exist - will show broken image icon for debugging
            console.log('Decorative image not found at /decorative-floral.png');
          }}
        />
        <img 
          src="/decorative-floral.png" 
          alt="" 
          className="decorative-floral decorative-floral-top-right"
          aria-hidden="true"
          onError={(e) => {
            // Fallback if image doesn't exist - will show broken image icon for debugging
            console.log('Decorative image not found at /decorative-floral.png');
          }}
        />
        <nav className="navbar">
          <button className="navbar-toggle" onClick={toggleMenu}>
            {menuOpen ? 'Close Menu' : 'Menu'}
          </button>
          <ul className={menuOpen ? "show" : ""}>
            <li><Link to="/" onClick={() => setMenuOpen(false)}>Home</Link></li>
            <li><Link to="/rsvp" onClick={() => setMenuOpen(false)}>RSVP</Link></li>
            {showMenuChoicesInNav && (
              <li><Link to="/menu-choices" onClick={() => setMenuOpen(false)}>Menu Choices</Link></li>
            )}
            <li><Link to="/accommodation" onClick={() => setMenuOpen(false)}>Accommodation</Link></li>
            <li><Link to="/location" onClick={() => setMenuOpen(false)}>Location</Link></li>
            <li><Link to="/photo-upload" onClick={() => setMenuOpen(false)}>Photo Upload</Link></li>
            <li><Link to="/faq" onClick={() => setMenuOpen(false)}>FAQ</Link></li>
            {isAdmin && (
            <>
              <li><Link to="/add-guest" onClick={() => setMenuOpen(false)}>Add Guest</Link></li>
              <li><Link to="/guest-list" onClick={() => setMenuOpen(false)}>Guest List</Link></li>
              <li><Link to="/user-logs" onClick={() => setMenuOpen(false)}>User Logs</Link></li>
            </>
          )}
            {user && (
              <li><Link to="/logout" onClick={() => setMenuOpen(false)}>Logout</Link></li>
            )}
            
          </ul>
        </nav>

        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/rsvp" element={<ProtectedRoute><RSVP /></ProtectedRoute>} />
          <Route path="/menu-choices" element={<ProtectedRoute><MenuChoices /></ProtectedRoute>} />
          <Route path="/accommodation" element={<ProtectedRoute><Accommodation /></ProtectedRoute>} />
          <Route path="/location" element={<ProtectedRoute><Location /></ProtectedRoute>} />
          <Route path="/photo-upload" element={<ProtectedRoute><PhotoUpload /></ProtectedRoute>} />
          <Route path="/faq" element={<ProtectedRoute><FAQ /></ProtectedRoute>} />
          <Route path="/faq/edit" element={<ProtectedRoute><FAQEdit /></ProtectedRoute>} />
          <Route path="/login" element={<Login />} />
          <Route path="/logout" element={<Logout />} />
          <Route path="/add-guest" element={<ProtectedRoute><AddGuest /></ProtectedRoute>} />
          <Route path="/guest-list" element={<ProtectedRoute><GuestList /></ProtectedRoute>} />
          <Route path="/user-logs" element={<ProtectedRoute><UserLogs /></ProtectedRoute>} />
        </Routes>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (authUser) => {
      setUser(authUser);
      if (authUser && authUser.phoneNumber) {
        await requestNotificationPermission(authUser.phoneNumber);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (messaging) {
      onMessage(messaging, (payload) => {
        if (payload.notification) {
          new Notification(payload.notification.title || 'New RSVP Update', {
            body: payload.notification.body || 'You have a new RSVP update',
            icon: '/logo192.png',
            badge: '/logo192.png',
            tag: 'rsvp-notification',
            requireInteraction: true
          });
        }
      });
    }
  }, []);

  const toggleMenu = () => setMenuOpen((open) => !open);

  return (
    <Router>
      <AppContent user={user} menuOpen={menuOpen} setMenuOpen={setMenuOpen} toggleMenu={toggleMenu} />
    </Router>
  );
}

export default App;